// Survey-level SWR fetches. Conditional keys skip fetching until the survey resolves and credit is not blocked.

import useSWR from "swr";

import { fetcherSimple } from "utils/api";
import { isSurveyCreditBlocked } from "../credit-helpers";
import { isStartedOrFinished } from "../lifecycle-visibility";

export function useSurveyData({ surveyId, respondentSentimentPage = 1 }) {
    const {
        data: survey,
        error: surveyError,
        isLoading: surveyIsLoading,
        mutate: surveyMutate,
    } = useSWR(
        [`/v2/survey/${surveyId}`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const isBlocked = isSurveyCreditBlocked(survey);

    const {
        data: surveyQuestions,
        isValidating: surveyQuestionsValidating,
        mutate: surveyQuestionsMutate,
    } = useSWR(
        () =>
            survey && !surveyError && !isBlocked
                ? [`/v2/survey/${surveyId}/question`, "GET"]
                : null,
        ([path, method]) => fetcherSimple(path, method),
    );

    const {
        data: surveyRecipients,
        error: surveyRecipientsError,
        mutate: recipientsMutate,
    } = useSWR(
        () =>
            survey && !surveyError && !isBlocked
                ? [`/v2/survey/${surveyId}/recipient/list`, "GET"]
                : null,
        ([path, method]) => fetcherSimple(path, method),
    );

    const {
        data: surveyAbuse,
        error: surveyAbuseError,
        mutate: surveyAbuseMutate,
    } = useSWR(
        () => {
            if (!isBlocked && isStartedOrFinished(survey?.state)) {
                return [`/v2/survey/${surveyId}/abuse_reports`, "GET"];
            }
            return null;
        },
        ([path, method]) => fetcherSimple(path, method),
    );

    const {
        data: surveyRespondentAnswers,
        error: surveyRespondentAnswersError,
        isValidating: surveyRespondentAnswersValidating,
        mutate: surveyRespondentAnswersMutate,
    } = useSWR(
        () =>
            survey && !surveyError && !isBlocked
                ? [`/v2/survey/${surveyId}/respondent_answers`, "GET"]
                : null,
        ([path, method]) => fetcherSimple(path, method),
    );

    const {
        data: respondentsResponsesSentiment,
        error: respondentsResponsesSentimentError,
        mutate: respondentsResponsesSentimentMutate,
    } = useSWR(
        () => {
            if (survey) {
                return [
                    `/v2/survey/${surveyId}/respondents/sentiment?page=${respondentSentimentPage}`,
                    "GET",
                ];
            }
            return null;
        },
        ([path, method]) => fetcherSimple(path, method),
    );

    return {
        survey,
        surveyError,
        surveyIsLoading,
        surveyMutate,
        isBlocked,
        surveyQuestions,
        surveyQuestionsValidating,
        surveyQuestionsMutate,
        surveyRecipients,
        surveyRecipientsError,
        recipientsMutate,
        surveyAbuse,
        surveyAbuseError,
        surveyAbuseMutate,
        surveyRespondentAnswers,
        surveyRespondentAnswersError,
        surveyRespondentAnswersValidating,
        surveyRespondentAnswersMutate,
        respondentsResponsesSentiment,
        respondentsResponsesSentimentError,
        respondentsResponsesSentimentMutate,
    };
}
